import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSite } from "../lib/SiteContext";
import { mergeContent } from "../lib/api";
import BrandWordmark from "./BrandWordmark";

export default function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { settings } = useSite();
  const content = mergeContent(settings);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const [contactActive, setContactActive] = useState(false);

  useEffect(() => {
    setContactActive(false);
  }, [pathname]);

  const goToSection = (id) => (e) => {
    e.preventDefault();
    if (document.getElementById(id)) {
      document.getElementById(id).scrollIntoView({ behavior: "smooth" });
      return;
    }
    navigate("/");
    setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }, 120);
  };

  const goHome = (e) => {
    e.preventDefault();
    if (pathname !== "/") {
      navigate("/");
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const linkCls = "link-line text-mute hover:text-bone transition-colors duration-300";

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-ink/85 backdrop-blur-md border-b border-line"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1440px] px-4 sm:px-6 md:px-10 pt-3 md:pt-4 pb-1 md:pb-1.5 flex flex-col items-center justify-center gap-5 md:gap-8">
        <BrandWordmark siteTitle={content.site_title} />
        <nav className="flex items-center justify-center gap-3 sm:gap-5 lg:gap-10 font-meta text-[9px] sm:text-[10px] lg:text-[11px] uppercase tracking-[0.25em]">
          {pathname !== "/studio" ? (
            <>
              <a
                href="/"
                onClick={goHome}
                data-testid="nav-gallery-link"
                className={`link-line transition-colors duration-300 whitespace-nowrap ${pathname === "/" ? "text-ochre" : "text-mute hover:text-bone"}`}
              >
                Gallery
              </a>
              <a
                href="/#works"
                onClick={goToSection("works")}
                data-testid="nav-collection-link"
                className={`hidden sm:inline-block ${linkCls}`}
              >
                Collection
              </a>
              <a
                href="/#about"
                onClick={goToSection("about")}
                data-testid="nav-about-link"
                className={`hidden sm:inline-block ${linkCls}`}
              >
                About
              </a>
              <Link
                to="/news"
                data-testid="nav-news-link"
                className={`link-line transition-colors duration-300 whitespace-nowrap ${pathname === "/news" ? "text-ochre" : "text-mute hover:text-bone"}`}
              >
                News
              </Link>
              <a
                href="/#contact"
                onClick={(e) => {
                  setContactActive(true);
                  goToSection("contact")(e);
                }}
                data-testid="nav-contact-button"
                className={`link-line whitespace-nowrap transition-colors duration-300 ${
                  contactActive ? "text-ochre" : "text-mute hover:text-bone"
                } active:text-ochre`}
              >
                Contact
              </a>
            </>
          ) : (
            <Link to="/" data-testid="nav-gallery-link" className={linkCls}>
              Gallery
            </Link>
          )}
        </nav>
      </div>
    </header>
  );
}
